<template>
  <div class="panel" data-qid="recurring-project-settings">
    <header class="panel__header">
      <div class="profile-compact">
        <div class="logo-container">
          <font-awesome-icon icon="cog" class="icon-svg--sm"/>
        </div>
        <div class="profile-compact__info">
          <div class="profile-compact__top">
            <div class="profile-compact__text">
              {{ $t('dashboard.settings') }}
            </div>
          </div>
        </div>
      </div>

      <div class="profile-compact__actions">
        <span class="icon-btn"
              v-if="canEdit && !editing"
              @click="startEditing"
              data-qid="recurring-settings-edit-button">
          <font-awesome-icon icon="pen"/>
        </span>
        <span class="icon-btn btn-delete"
              v-if="editing"
              @click="editing = false"
              data-qid="recurring-settings-cancel-button">
          <font-awesome-icon icon="rotate-left"/>
        </span>
        <span class="icon-btn btn-edit"
              v-if="editing"
              @click="save"
              data-qid="recurring-settings-save-button">
          <font-awesome-icon icon="save"/>
        </span>
      </div>
    </header>

    <div class="panel__body">
      <!-- Set when the project is created -->
      <div class="engagement-fixed-settings">
        <div class="engagement-fixed-settings__hint">
          <font-awesome-icon icon="lock" class="mr-1"/>{{ $t('projects.recurring.settings.fixed_hint') }}
        </div>
        <div class="engagement-facts mb-0">
          <div class="engagement-facts__item">
            <span class="engagement-facts__label">{{ $t('table.type') }}</span>
            <span>{{ $t('projects.management.types.' + project.type) }}</span>
          </div>
          <div class="engagement-facts__item" v-if="project.assessment">
            <span class="engagement-facts__label">{{ $t('table.template') }}</span>
            <span>{{ project.assessment.name }}</span>
          </div>
          <div class="engagement-facts__item" v-if="project.scope">
            <span class="engagement-facts__label">{{ $t('table.scope') }}</span>
            <span>{{ $t('projects.launcher.scopes.' + project.scope) }}</span>
          </div>
          <div class="engagement-facts__item">
            <span class="engagement-facts__label">{{ $t('projects.create.schedule_label') }}</span>
            <span>{{ $t('projects.management.recurring') }}</span>
          </div>
        </div>
      </div>

      <div class="engagement-facts">
        <div class="engagement-facts__item">
          <span class="engagement-facts__label">{{ $t('table.last_updated') }}</span>
          <span>{{ project.updated_at | formatDate }}</span>
          <span v-if="project.updated_by" class="text-muted">· {{ project.updated_by.name }}</span>
        </div>
        <div class="engagement-facts__item" v-if="project.tags_translated && project.tags_translated.length">
          <span class="engagement-facts__label">{{ $t('table.tags') }}</span>
          <span class="badge" v-for="(tag, index) in project.tags_translated" :key="'tag-' + index">
            <font-awesome-icon icon="tag"/> {{ tag.name_translated | truncate(35) }}
          </span>
        </div>
      </div>

      <b-alert v-if="error.length"
               show
               variant="danger"
               class="mt-2">{{ error }}</b-alert>

      <!-- Copied into every new round -->
      <div class="engagement-settings-list">
        <div class="engagement-settings-list__row">
          <label>{{ $t('projects.create.reporting_language') }}</label>
          <multiselect v-if="editing"
                       v-model="form.language"
                       :options="languages"
                       select-label=""
                       selected-label=""
                       deselect-label=""
                       label="text"
                       track-by="value"
                       :placeholder="$t('processes.create.language_placeholder')"
          ></multiselect>
          <span v-else-if="currentLanguage">{{ currentLanguage.text }}</span>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>

        <div class="engagement-settings-list__row" v-if="hasProjectType">
          <label>{{ $t('projects.create.project_type') }}</label>
          <b-form-radio-group v-if="editing" v-model="form.project_type" :options="typeOptions"></b-form-radio-group>
          <b v-else-if="project.project_type">{{ project.project_type === 'type two' ? $t('system.type_two') : $t('system.type_one') }}</b>
          <span v-else class="text-muted">{{ $t('system.missing_input') }}</span>
        </div>

        <div class="engagement-settings-list__row" v-for="field in yesNoFields" :key="field.key">
          <label>{{ $t(field.label) }}</label>
          <b-form-radio-group v-if="editing" v-model="form[field.key]" :options="yesNoOptions"></b-form-radio-group>
          <span v-else :class="project[field.key] == 1 ? 'engagement-setting-on' : 'engagement-setting-off'">
            <font-awesome-icon :icon="project[field.key] == 1 ? 'check-circle' : 'minus-circle'" class="mr-1"/>
            {{ project[field.key] == 1 ? $t('system.yes') : $t('system.no') }}
          </span>
        </div>

        <div class="engagement-settings-list__row" v-if="isAudit">
          <label>{{ $t('system.required_sign_offs') }}</label>
          <div v-if="editing">
            <NumberStepper v-model="form.required_sign_offs" qid="recurring-settings-required-sign-offs"></NumberStepper>
            <small class="form-text text-muted">{{ $t('system.required_sign_offs_description') }}</small>
          </div>
          <span v-else>{{ project.required_sign_offs ? project.required_sign_offs : 0 }}</span>
        </div>

        <div class="engagement-settings-list__row">
          <label>{{ $t('projects.create.external_link_label') }}</label>
          <multiselect v-if="editing"
                       v-model="form.external_client"
                       :options="clients"
                       :internal-search="false"
                       @search-change="loadClients"
                       select-label=""
                       selected-label=""
                       :deselect-label="$t('projects.create.external_link_remove_button')"
                       label="name"
                       track-by="slug"
                       :placeholder="$t('projects.create.external_link_label')"
          >
            <template slot="singleLabel" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">{{ props.option.name }}</span>
            </template>
            <template slot="option" slot-scope="props">
              <Avatar :object="props.option" size="xs"></Avatar>
              <span class="ml-2">{{ props.option.name }}</span>
            </template>
          </multiselect>
          <span v-else-if="linkedClient">
            <Avatar :object="linkedClient" size="xs"></Avatar>
            <span class="ml-1">{{ linkedClient.name }}</span>
          </span>
          <span v-else class="text-muted">{{ $t('system.n_a') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import Avatar from '@/components/Avatar.vue';
import NumberStepper from '@/components/NumberStepper.vue';

// Settings of a recurring project, shown like an engagement's settings and copied into its rounds (US 9396)
export default {
  name: 'RecurringProjectSettings',
  props: {
    project: {
      type: Object,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Avatar,
    NumberStepper
  },
  data: function() {
    return {
      editing: false,
      error: '',
      form: {},
      clients: [],
      yesNoOptions: [
        { text: this.$t('system.yes'), value: 1 },
        { text: this.$t('system.no'), value: 0 }
      ],
      typeOptions: [
        { text: this.$t('system.type_one'), value: 'type one' },
        { text: this.$t('system.type_two'), value: 'type two' }
      ],
      languages: [
        {value: 'en', text: 'English'},
        {value: 'da', text: 'Danish'},
        {value: 'fr', text: 'French'},
        {value: 'de', text: 'German'},
        {value: 'sv', text: 'Swedish'},
        {value: 'nl', text: 'Dutch'},
        {value: 'es', text: 'Spanish'}
      ]
    }
  },
  computed: {
    getUser() {
      return this.$store.state.user;
    },
    isAudit() {
      return this.project.type === 'control_assessment';
    },
    hasProjectType() {
      return this.project.type === 'control_assessment' || this.project.type === 'risk_assessment';
    },
    // The same fields as the Edit project modal had, per project type
    yesNoFields() {
      let fields = [{ key: 'include_relations', label: 'projects.create.include_relations' }];
      if (this.isAudit) {
        fields.push({ key: 'include_mapping', label: 'maintenance.assessments.include_mapping_title' });
        fields.push({ key: 'include_independence_declaration', label: 'maintenance.assessments.require_independence_declaration_title' });
      }
      fields.push({ key: 'overdue_notifications', label: 'projects.create.overdue_reminder' });
      return fields;
    },
    // Seen from the linked client, the link points back to the client that owns the project
    linkedClient() {
      const client = this.project.additional_client;
      if (!client || !client.slug) {
        return null;
      }
      const currentClient = this.$store.state.client;
      return currentClient && currentClient.slug === client.slug && this.project.active_client ? this.project.active_client : client;
    },
    currentLanguage() {
      return _.find(this.languages, language => language.value === this.project.language) || null;
    }
  },
  methods: {
    startEditing() {
      this.error = '';
      this.form = {
        language: this.currentLanguage,
        project_type: this.project.project_type,
        include_relations: this.project.include_relations ? 1 : 0,
        include_mapping: this.project.include_mapping ? 1 : 0,
        include_independence_declaration: this.project.include_independence_declaration ? 1 : 0,
        overdue_notifications: this.project.overdue_notifications ? 1 : 0,
        required_sign_offs: this.project.required_sign_offs ? this.project.required_sign_offs : 0,
        external_client: this.project.additional_client && this.project.additional_client.slug ? this.project.additional_client : null
      };
      this.loadClients();
      this.editing = true;
    },
    loadClients(query) {
      let self = this;
      piincHttp.get('clients', { params: {
        per_page: 100,
        without_partition: 1,
        member: this.getUser && !this.getUser.isAdmin ? this.getUser.id : undefined,
        keyword: query ? query : undefined
      }}).then(function(response) {
        self.clients = response.data.items;
      }, function() {});
    },
    save() {
      let self = this;
      let data = {
        language: this.form.language ? this.form.language.value : null,
        include_relations: this.form.include_relations,
        overdue_notifications: this.form.overdue_notifications,
        external_client: this.form.external_client ? this.form.external_client.slug : ''
      };
      if (this.hasProjectType) {
        data.project_type = this.form.project_type;
      }
      if (this.isAudit) {
        data.include_mapping = this.form.include_mapping;
        data.include_independence_declaration = this.form.include_independence_declaration;
        data.required_sign_offs = this.form.required_sign_offs;
      }
      piincHttp.put('projects/' + this.project.slug, data).then(function(response) {
        self.editing = false;
        self.$toastr('success', self.$t('projects.update.success'));
        self.$emit('updated', response.data);
      }, function(error) {
        self.error = error.data && error.data.message ? error.data.message : '';
      });
    }
  }
}
</script>
